import React, { useEffect, useRef } from "react";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Pilcrow,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Link,
  Unlink,
  Minus,
  RemoveFormatting,
  Undo,
  Redo,
} from "lucide-react";
import { isHtml, sanitizeHtml } from "../../services/richText";

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  minHeight?: number;
}

const escapeText = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Older descriptions are plain text; turn their line breaks into paragraphs
const toEditorHtml = (value: string) => {
  if (!value) return "";
  if (isHtml(value)) return sanitizeHtml(value);
  return value
    .split(/\n{2,}/)
    .map((para) => `<p>${escapeText(para).replace(/\n/g, "<br>")}</p>`)
    .join("");
};

type ToolbarAction = {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  run: () => void;
};

export function RichTextEditor({ value, onChange, placeholder, disabled, minHeight = 180 }: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastEmitted = useRef<string | null>(null);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    // Re-seed when the value changes externally or a fresh editor element mounts
    if (value === lastEmitted.current && el.dataset.ready === "1") return;
    el.innerHTML = toEditorHtml(value);
    el.dataset.ready = "1";
    lastEmitted.current = value;
  }, [value, disabled]);

  const emit = () => {
    const el = editorRef.current;
    if (!el) return;
    const html = el.textContent?.trim() || el.querySelector("li, hr") ? sanitizeHtml(el.innerHTML) : "";
    lastEmitted.current = html;
    onChange(html);
  };

  const exec = (command: string, arg?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const addLink = () => {
    const url = window.prompt("Link address (https://...)", "https://");
    if (!url) return;
    if (!/^(https?:\/\/|mailto:)/i.test(url.trim())) {
      window.alert("Links must start with https://, http:// or mailto:");
      return;
    }
    exec("createLink", url.trim());
  };

  const groups: ToolbarAction[][] = [
    [
      { label: "Bold (Ctrl+B)", icon: Bold, run: () => exec("bold") },
      { label: "Italic (Ctrl+I)", icon: Italic, run: () => exec("italic") },
      { label: "Underline (Ctrl+U)", icon: Underline, run: () => exec("underline") },
      { label: "Strikethrough", icon: Strikethrough, run: () => exec("strikeThrough") },
    ],
    [
      { label: "Paragraph", icon: Pilcrow, run: () => exec("formatBlock", "<p>") },
      { label: "Heading", icon: Heading2, run: () => exec("formatBlock", "<h2>") },
      { label: "Sub-heading", icon: Heading3, run: () => exec("formatBlock", "<h3>") },
      { label: "Quote", icon: Quote, run: () => exec("formatBlock", "<blockquote>") },
    ],
    [
      { label: "Bulleted list", icon: List, run: () => exec("insertUnorderedList") },
      { label: "Numbered list", icon: ListOrdered, run: () => exec("insertOrderedList") },
    ],
    [
      { label: "Align left", icon: AlignLeft, run: () => exec("justifyLeft") },
      { label: "Align center", icon: AlignCenter, run: () => exec("justifyCenter") },
      { label: "Align right", icon: AlignRight, run: () => exec("justifyRight") },
      { label: "Justify", icon: AlignJustify, run: () => exec("justifyFull") },
    ],
    [
      { label: "Insert link", icon: Link, run: addLink },
      { label: "Remove link", icon: Unlink, run: () => exec("unlink") },
      { label: "Horizontal line", icon: Minus, run: () => exec("insertHorizontalRule") },
      { label: "Clear formatting", icon: RemoveFormatting, run: () => exec("removeFormat") },
    ],
    [
      { label: "Undo (Ctrl+Z)", icon: Undo, run: () => exec("undo") },
      { label: "Redo (Ctrl+Y)", icon: Redo, run: () => exec("redo") },
    ],
  ];

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    const content = html ? sanitizeHtml(html) : toEditorHtml(text);
    document.execCommand("insertHTML", false, content);
    emit();
  };

  if (disabled) {
    return (
      <div
        className="rich-text border rounded-md px-3 py-2 bg-gray-50 text-sm text-gray-700"
        dangerouslySetInnerHTML={{ __html: toEditorHtml(value) || "<p>-</p>" }}
      />
    );
  }

  return (
    <div className="border rounded-md bg-white focus-within:ring-2 focus-within:ring-blue-500">
      <div className="flex flex-wrap items-center gap-1 border-b bg-gray-50 px-2 py-1" role="toolbar" aria-label="Text formatting">
        {groups.map((group, gi) => (
          <div key={gi} className="flex items-center gap-0.5 pr-1 mr-1 border-r last:border-r-0">
            {group.map(({ label, icon: Icon, run }) => (
              <button
                key={label}
                type="button"
                title={label}
                aria-label={label}
                onMouseDown={(e) => e.preventDefault()}
                onClick={run}
                className="p-1.5 rounded hover:bg-gray-200 text-gray-700 cursor-pointer"
              >
                <Icon className="w-4 h-4" />
              </button>
            ))}
          </div>
        ))}
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={placeholder || "Description"}
        data-placeholder={placeholder}
        onFocus={() => document.execCommand("defaultParagraphSeparator", false, "p")}
        onInput={emit}
        onBlur={emit}
        onPaste={handlePaste}
        className="rich-text rich-text-editor px-3 py-2 text-sm outline-none overflow-y-auto"
        style={{ minHeight, maxHeight: 480 }}
      />
    </div>
  );
}
